// =============================================================================
// Admin Messages Page
// =============================================================================

import { useState, useEffect } from 'react';
import api from '../../services/api';
import { formatMessageDate, formatDateTime } from '../../utils/dateFormat';
import { ShieldExclamationIcon } from '@heroicons/react/24/outline';
import { usePermissions } from '../../hooks/usePermissions';

// =============================================================================
// Types
// =============================================================================

interface Attachment {
  id: string;
  name: string;
  mimeType?: string;
  fileSize?: number;
}

interface Message {
  id: string;
  fromUserId: string;
  fromUserName: string;
  fromUserEmail: string;
  fromUserRole?: string;
  toUserId?: string;
  toUserName?: string;
  toUserEmail?: string;
  toUserRole?: string;
  subject: string;
  body: string;
  readAt?: string;
  archivedAt?: string;
  createdAt: string;
  isSentByAdmin?: boolean;
  attachments?: Attachment[];
}

interface Investor {
  id: string;
  name: string;
  email: string;
}

type FilterType = 'inbox' | 'sent' | 'archived';

// =============================================================================
// Component
// =============================================================================

export function MessagesPage() {
  const { hasPermission, isLoaded: permsLoaded } = usePermissions();
  const canCreate = hasPermission('communications', 'create');
  const canDelete = hasPermission('communications', 'delete');
  const [messages, setMessages] = useState<Message[]>([]);
  const [allMessages, setAllMessages] = useState<Message[]>([]); // Store unfiltered messages for investor extraction
  const [investors, setInvestors] = useState<Investor[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Investor filter - only investors who have messaged
  const [investorsWithMessages, setInvestorsWithMessages] = useState<Investor[]>([]);
  const [selectedInvestorFilter, setSelectedInvestorFilter] = useState<string>('');

  // Selected message
  const [selectedMessage, setSelectedMessage] = useState<Message | null>(null);

  // Compose modal
  const [showComposeModal, setShowComposeModal] = useState(false);
  const [sending, setSending] = useState(false);
  const [sendError, setSendError] = useState<string | null>(null);

  // Compose form
  const [composeToId, setComposeToId] = useState('');
  const [composeSubject, setComposeSubject] = useState('');
  const [composeBody, setComposeBody] = useState('');
  const [replyToId, setReplyToId] = useState<string | null>(null);
  const [composeFiles, setComposeFiles] = useState<File[]>([]);

  // Filter - default to inbox
  const [filter, setFilter] = useState<FilterType>('inbox');

  // Archive confirmation modal
  const [showArchiveConfirm, setShowArchiveConfirm] = useState(false);
  const [archiveTarget, setArchiveTarget] = useState<Message | null>(null);
  const [archiving, setArchiving] = useState(false);

  useEffect(() => {
    loadMessages();
    loadInvestors();
  }, [filter]);

  // Apply investor filter to messages
  useEffect(() => {
    if (selectedInvestorFilter) {
      const filtered = allMessages.filter((m) => {
        // For inbox: filter by sender
        // For sent: filter by recipient
        // For archived: filter by either
        if (filter === 'sent') {
          return m.toUserId === selectedInvestorFilter;
        } else if (filter === 'inbox') {
          return m.fromUserId === selectedInvestorFilter;
        } else {
          return m.fromUserId === selectedInvestorFilter || m.toUserId === selectedInvestorFilter;
        }
      });
      setMessages(filtered);
    } else {
      setMessages(allMessages);
    }
  }, [selectedInvestorFilter, allMessages, filter]);

  const loadMessages = async () => {
    try {
      setLoading(true);
      setError(null);
      // Map 'inbox' to 'all' for the API (inbox = all non-archived, non-sent)
      const apiFilter = filter === 'inbox' ? 'all' : filter;
      const response = await api.get<{ success: boolean; data: Message[] }>(
        `/admin/messages?filter=${apiFilter}`
      );
      const loadedMessages = response.data.data || [];
      setAllMessages(loadedMessages);
      setMessages(loadedMessages);

      // Extract unique investors from messages
      const investorMap = new Map<string, Investor>();
      loadedMessages.forEach((m) => {
        // From sender (if investor sent message)
        if (m.fromUserId && m.fromUserRole === 'INVESTOR' && m.fromUserName) {
          investorMap.set(m.fromUserId, {
            id: m.fromUserId,
            name: m.fromUserName,
            email: m.fromUserEmail,
          });
        }
        // From recipient (if admin sent to investor)
        if (m.toUserId && m.toUserRole === 'INVESTOR' && m.toUserName) {
          investorMap.set(m.toUserId, {
            id: m.toUserId,
            name: m.toUserName,
            email: m.toUserEmail || '',
          });
        }
      });
      setInvestorsWithMessages(Array.from(investorMap.values()).sort((a, b) => a.name.localeCompare(b.name)));
    } catch (err) {
      setError('Failed to load messages');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const loadInvestors = async () => {
    try {
      const response = await api.get<{ success: boolean; data: Investor[] }>('/admin/investors?limit=500');
      setInvestors(response.data.data || []);
    } catch (err) {
      console.error('Failed to load investors:', err);
    }
  };

  const handleSendMessage = async () => {
    if (!composeSubject || !composeBody) {
      setSendError('Please fill in subject and message');
      return;
    }

    try {
      setSending(true);
      setSendError(null);

      const formData = new FormData();
      if (composeToId) formData.append('toUserId', composeToId);
      formData.append('subject', composeSubject);
      formData.append('body', composeBody);
      if (replyToId) formData.append('parentMessageId', replyToId);
      composeFiles.forEach((file) => formData.append('files', file));

      await api.post('/admin/messages', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });

      setShowComposeModal(false);
      resetComposeForm();
      loadMessages();
    } catch (err: unknown) {
      const error = err as { response?: { data?: { error?: string } } };
      setSendError(error.response?.data?.error || 'Failed to send message');
    } finally {
      setSending(false);
    }
  };

  const handleMarkAsRead = async (messageId: string) => {
    try {
      // Instantly decrement sidebar badge + notification bell (no API wait)
      window.dispatchEvent(new CustomEvent('badge-decrement', { detail: { messages: -1, messageId } }));
      // Optimistic update - mark as read in local state immediately
      const now = new Date().toISOString();
      setAllMessages((prev) =>
        prev.map((m) => (m.id === messageId ? { ...m, readAt: now } : m))
      );
      // Then persist to server
      await api.put(`/admin/messages/${messageId}/read`);
    } catch (err) {
      console.error('Failed to mark as read:', err);
    }
  };

  const handleArchiveClick = (message: Message) => {
    setArchiveTarget(message);
    setShowArchiveConfirm(true);
  };

  const handleArchiveConfirm = async () => {
    if (!archiveTarget) return;

    try {
      setArchiving(true);
      await api.put(`/admin/messages/${archiveTarget.id}/archive`);
      setShowArchiveConfirm(false);
      setArchiveTarget(null);
      setSelectedMessage(null);
      loadMessages();
    } catch (err) {
      console.error('Failed to archive message:', err);
    } finally {
      setArchiving(false);
    }
  };

  const handleUnarchive = async (messageId: string) => {
    try {
      await api.put(`/admin/messages/${messageId}/unarchive`);
      loadMessages();
      setSelectedMessage(null);
    } catch (err) {
      console.error('Failed to unarchive message:', err);
    }
  };

  const handleReply = (message: Message) => {
    setReplyToId(message.id);
    setComposeToId(message.fromUserId);
    setComposeSubject(`Re: ${message.subject}`);
    setShowComposeModal(true);
  };

  const resetComposeForm = () => {
    setComposeToId('');
    setComposeSubject('');
    setComposeBody('');
    setComposeFiles([]);
    setReplyToId(null);
    setSendError(null);
  };

  // Use centralized PST date formatting
  const formatDate = formatMessageDate;

  // Count unread (only from inbox messages)
  const unreadCount = messages.filter((m) => !m.readAt && !m.isSentByAdmin).length;

  // Permission guard - early return after all hooks
  if (permsLoaded && !hasPermission('communications', 'view')) {
    return (
      <div className="flex flex-col items-center justify-center py-16">
        <ShieldExclamationIcon className="h-12 w-12 text-gray-400 mb-3" />
        <h3 className="text-lg font-medium text-gray-900">Access Denied</h3>
        <p className="mt-1 text-sm text-gray-500">You do not have permission to view this page.</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Messages</h1>
          <p className="text-gray-500 mt-1">
            Communicate with investors
            {unreadCount > 0 && (
              <span className="ml-2 px-2 py-0.5 bg-red-100 text-red-800 rounded-full text-sm">
                {unreadCount} unread
              </span>
            )}
          </p>
        </div>
        {canCreate && (
          <button
            onClick={() => setShowComposeModal(true)}
            className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 flex items-center gap-2"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
            </svg>
            Compose
          </button>
        )}
      </div>

      {/* Filter Tabs - Inbox, Sent, Archived */}
      <div className="flex items-center justify-between border-b">
        <div className="flex gap-4">
          <button
            onClick={() => { setFilter('inbox'); setSelectedInvestorFilter(''); }}
            className={`pb-3 px-1 font-medium text-sm border-b-2 flex items-center gap-2 ${
              filter === 'inbox'
                ? 'border-primary-600 text-primary-600'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            Inbox
            {filter === 'inbox' && unreadCount > 0 && (
              <span className="px-1.5 py-0.5 bg-red-500 text-white text-xs rounded-full">
                {unreadCount}
              </span>
            )}
          </button>
          <button
            onClick={() => { setFilter('sent'); setSelectedInvestorFilter(''); }}
            className={`pb-3 px-1 font-medium text-sm border-b-2 ${
              filter === 'sent'
                ? 'border-primary-600 text-primary-600'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            Sent
          </button>
          <button
            onClick={() => { setFilter('archived'); setSelectedInvestorFilter(''); }}
            className={`pb-3 px-1 font-medium text-sm border-b-2 ${
              filter === 'archived'
                ? 'border-primary-600 text-primary-600'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            Archived
          </button>
        </div>

        {/* Investor Filter Dropdown */}
        {investorsWithMessages.length > 0 && (
          <div className="pb-2">
            <select
              value={selectedInvestorFilter}
              onChange={(e) => setSelectedInvestorFilter(e.target.value)}
              className="px-3 py-1.5 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
            >
              <option value="">All Investors</option>
              {investorsWithMessages.map((investor) => (
                <option key={investor.id} value={investor.id}>
                  {investor.name}
                </option>
              ))}
            </select>
          </div>
        )}
      </div>

      {/* Split-Pane Layout */}
      {loading ? (
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-900" />
        </div>
      ) : error ? (
        <div className="text-center py-12 text-red-600">{error}</div>
      ) : messages.length === 0 && !showComposeModal ? (
        <div className="bg-white rounded-lg shadow p-12 text-center">
          <svg className="w-12 h-12 text-gray-400 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
          </svg>
          <p className="text-gray-500">
            {filter === 'archived' ? 'No archived messages' :
             filter === 'sent' ? 'No sent messages' : 'No messages in inbox'}
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Message List - Left Pane */}
          <div className="lg:col-span-1">
            <div className="bg-white rounded-lg shadow divide-y max-h-[600px] overflow-y-auto">
              {messages.map((message) => (
                <div
                  key={message.id}
                  onClick={() => {
                    setSelectedMessage(message);
                    if (!message.readAt && !message.isSentByAdmin) {
                      handleMarkAsRead(message.id);
                    }
                  }}
                  className={`p-4 cursor-pointer hover:bg-gray-50 ${
                    selectedMessage?.id === message.id ? 'bg-primary-50' : ''
                  } ${!message.readAt && !message.isSentByAdmin ? 'border-l-4 border-l-primary-600' : ''}`}
                >
                  <div className="flex items-start gap-3">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <h3 className={`text-sm truncate ${!message.readAt && !message.isSentByAdmin ? 'font-semibold' : 'font-medium'} text-gray-900`}>
                          {message.subject}
                        </h3>
                      </div>
                      <p className="text-xs text-gray-500 mt-1">
                        {message.isSentByAdmin
                          ? `To: ${message.toUserName || message.toUserEmail || 'All Investors'}`
                          : `From: ${message.fromUserName || message.fromUserEmail}`}
                      </p>
                      <p className="text-xs text-gray-400 mt-0.5">
                        {formatDate(message.createdAt)}
                      </p>
                    </div>
                    {!message.readAt && !message.isSentByAdmin && (
                      <span className="w-2 h-2 bg-primary-600 rounded-full flex-shrink-0 mt-1.5" />
                    )}
                    {message.isSentByAdmin && (
                      <span className="text-xs text-gray-400">Sent</span>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Message Detail - Right Pane */}
          <div className="lg:col-span-2">
            {selectedMessage ? (
              <div className="bg-white rounded-lg shadow p-6">
                <div className="mb-4">
                  <h2 className="text-xl font-semibold text-gray-900">{selectedMessage.subject}</h2>
                  <div className="flex items-center gap-4 mt-2 text-sm text-gray-500">
                    <span>
                      {selectedMessage.isSentByAdmin
                        ? `To: ${selectedMessage.toUserName || selectedMessage.toUserEmail || 'All Investors'}`
                        : `From: ${selectedMessage.fromUserName || selectedMessage.fromUserEmail}`}
                    </span>
                    <span>{formatDateTime(selectedMessage.createdAt)}</span>
                  </div>
                </div>
                <div className="prose max-w-none border-t pt-4 pb-4">
                  <p className="whitespace-pre-wrap">{selectedMessage.body}</p>
                </div>
                {/* Attachments */}
                {selectedMessage.attachments && selectedMessage.attachments.length > 0 && (
                  <div className="pb-4">
                    <p className="text-sm font-medium text-gray-700 mb-2">Attachments ({selectedMessage.attachments.length})</p>
                    <div className="space-y-1">
                      {selectedMessage.attachments.map((att) => (
                        <button
                          key={att.id}
                          onClick={async () => {
                            try {
                              const response = await api.get(
                                `/admin/messages/${selectedMessage.id}/attachments/${att.id}/download`,
                                { responseType: 'blob' }
                              );
                              const url = window.URL.createObjectURL(response.data);
                              const a = document.createElement('a');
                              a.href = url;
                              a.download = att.name;
                              document.body.appendChild(a);
                              a.click();
                              document.body.removeChild(a);
                              window.URL.revokeObjectURL(url);
                            } catch {
                              setError('Failed to download attachment');
                            }
                          }}
                          className="flex items-center gap-2 px-3 py-2 bg-gray-50 rounded-lg hover:bg-gray-100 text-sm w-full text-left"
                        >
                          <svg className="w-4 h-4 text-gray-500 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.172 7l-6.586 6.586a2 2 0 102.828 2.828l6.414-6.586a4 4 0 00-5.656-5.656l-6.415 6.585a6 6 0 108.486 8.486L20.5 13" />
                          </svg>
                          <span className="truncate">{att.name}</span>
                          {att.fileSize && (
                            <span className="text-gray-400 text-xs flex-shrink-0">
                              ({att.fileSize > 1048576 ? `${(att.fileSize / 1048576).toFixed(1)} MB` : `${(att.fileSize / 1024).toFixed(0)} KB`})
                            </span>
                          )}
                          <svg className="w-4 h-4 text-primary-500 ml-auto flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                          </svg>
                        </button>
                      ))}
                    </div>
                  </div>
                )}
                {/* Action buttons */}
                <div className="flex justify-between pt-4 border-t">
                  <div className="flex gap-2">
                    {canDelete && (selectedMessage.archivedAt || filter === 'archived' ? (
                      <button
                        onClick={() => handleUnarchive(selectedMessage.id)}
                        className="px-3 py-2 text-sm border rounded-lg hover:bg-gray-50 flex items-center gap-2"
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 8h14M5 8a2 2 0 110-4h14a2 2 0 110 4M5 8v10a2 2 0 002 2h10a2 2 0 002-2V8m-9 4h4" />
                        </svg>
                        Move to Inbox
                      </button>
                    ) : (
                      <button
                        onClick={() => handleArchiveClick(selectedMessage)}
                        className="px-3 py-2 text-sm border rounded-lg hover:bg-gray-50 flex items-center gap-2"
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 8h14M5 8a2 2 0 110-4h14a2 2 0 110 4M5 8v10a2 2 0 002 2h10a2 2 0 002-2V8m-9 4h4" />
                        </svg>
                        Archive
                      </button>
                    ))}
                  </div>
                  <div className="flex gap-2">
                    {!selectedMessage.isSentByAdmin && canCreate && (
                      <button
                        onClick={() => {
                          handleReply(selectedMessage);
                          setSelectedMessage(null);
                        }}
                        className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 flex items-center gap-2"
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h10a8 8 0 018 8v2M3 10l6 6m-6-6l6-6" />
                        </svg>
                        Reply
                      </button>
                    )}
                    <button
                      onClick={() => setSelectedMessage(null)}
                      className="px-4 py-2 border rounded-lg hover:bg-gray-50"
                    >
                      Close
                    </button>
                  </div>
                </div>
              </div>
            ) : (
              <div className="bg-white rounded-lg shadow p-12 text-center">
                <svg className="w-12 h-12 text-gray-400 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-5 5v-5z" />
                </svg>
                <p className="text-gray-500">Select a message to read</p>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Archive Confirmation Modal */}
      {showArchiveConfirm && archiveTarget && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-sm w-full p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="p-2 bg-gray-100 rounded-full">
                <svg className="w-6 h-6 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 8h14M5 8a2 2 0 110-4h14a2 2 0 110 4M5 8v10a2 2 0 002 2h10a2 2 0 002-2V8m-9 4h4" />
                </svg>
              </div>
              <h3 className="text-lg font-semibold text-gray-900">Archive Message</h3>
            </div>
            <p className="text-gray-600 mb-6">
              Are you sure you want to archive this message? You can find it later in the Archived tab.
            </p>
            <div className="flex justify-end gap-3">
              <button
                onClick={() => {
                  setShowArchiveConfirm(false);
                  setArchiveTarget(null);
                }}
                className="px-4 py-2 border rounded-lg hover:bg-gray-50"
                disabled={archiving}
              >
                Cancel
              </button>
              <button
                onClick={handleArchiveConfirm}
                disabled={archiving}
                className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50 flex items-center gap-2"
              >
                {archiving ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white" />
                    Archiving...
                  </>
                ) : (
                  'Archive'
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Compose Modal */}
      {showComposeModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full">
            <div className="p-6">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-xl font-semibold">
                  {replyToId ? 'Reply to Message' : 'New Message'}
                </h2>
                <button
                  onClick={() => {
                    setShowComposeModal(false);
                    resetComposeForm();
                  }}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>

              {sendError && (
                <div className="mb-4 p-3 bg-red-50 text-red-600 rounded-lg text-sm">
                  {sendError}
                </div>
              )}

              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">To</label>
                  {replyToId ? (
                    <div className="w-full px-3 py-2 border rounded-lg bg-gray-100 text-gray-700">
                      {investors.find((i) => i.id === composeToId)?.name || composeToId}
                    </div>
                  ) : (
                    <select
                      value={composeToId}
                      onChange={(e) => setComposeToId(e.target.value)}
                      className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                    >
                      <option value="">All Investors (Broadcast)</option>
                      {investors.map((investor) => (
                        <option key={investor.id} value={investor.id}>
                          {investor.name} ({investor.email})
                        </option>
                      ))}
                    </select>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Subject *</label>
                  <input
                    type="text"
                    value={composeSubject}
                    onChange={(e) => setComposeSubject(e.target.value)}
                    placeholder="Message subject"
                    className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Message *</label>
                  <textarea
                    value={composeBody}
                    onChange={(e) => setComposeBody(e.target.value)}
                    placeholder="Write your message..."
                    rows={8}
                    className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                  />
                </div>

                {/* File Attachments */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Attachments</label>
                  <label className="inline-flex items-center gap-2 px-3 py-2 border border-dashed border-gray-300 rounded-lg cursor-pointer hover:bg-gray-50 text-sm text-gray-600">
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.172 7l-6.586 6.586a2 2 0 102.828 2.828l6.414-6.586a4 4 0 00-5.656-5.656l-6.415 6.585a6 6 0 108.486 8.486L20.5 13" />
                    </svg>
                    Attach Files
                    <input
                      type="file"
                      multiple
                      className="hidden"
                      onChange={(e) => {
                        const selected = e.target.files ? Array.from(e.target.files) : [];
                        if (selected.length > 0) {
                          setComposeFiles((prev) => [...prev, ...selected]);
                        }
                        e.target.value = '';
                      }}
                    />
                  </label>
                  {composeFiles.length > 0 && (
                    <div className="mt-2 space-y-1">
                      {composeFiles.map((file, idx) => (
                        <div key={idx} className="flex items-center justify-between px-3 py-1.5 bg-gray-50 rounded text-sm">
                          <span className="truncate mr-2">{file.name} ({(file.size / 1024).toFixed(0)} KB)</span>
                          <button
                            type="button"
                            onClick={() => setComposeFiles((prev) => prev.filter((_, i) => i !== idx))}
                            className="text-red-400 hover:text-red-600 flex-shrink-0"
                          >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                            </svg>
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                <p className="text-xs text-gray-500">
                  An email notification will be sent to the recipient.
                </p>
              </div>

              <div className="flex justify-end gap-3 mt-6 pt-4 border-t">
                <button
                  onClick={() => {
                    setShowComposeModal(false);
                    resetComposeForm();
                  }}
                  className="px-4 py-2 border rounded-lg hover:bg-gray-50"
                  disabled={sending}
                >
                  Cancel
                </button>
                <button
                  onClick={handleSendMessage}
                  disabled={sending || !composeSubject || !composeBody}
                  className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                >
                  {sending ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white" />
                      Sending...
                    </>
                  ) : (
                    <>
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" />
                      </svg>
                      Send Message
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default MessagesPage;
